import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Sparkles, PlusCircle, Link2, Building2, ArrowRight } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh, user, logout } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const [step, setStep] = useState("choice"); // choice | labrand | manual
  const [labrandStatus, setLabrandStatus] = useState(null);
  const [labrandBrands, setLabrandBrands] = useState([]);
  const [labrandBase, setLabrandBase] = useState("");
  const [labrandKey, setLabrandKey] = useState("");
  const [form, setForm] = useState({ name: "", handle: "", industry: "", audience: "" });
  const [loading, setLoading] = useState(false);
  const [existingBrands, setExistingBrands] = useState([]);

  useEffect(() => {
    api.get("/labrand/status").then((r) => setLabrandStatus(r.data)).catch(() => setLabrandStatus({ configured: false }));
    api.get("/brands").then((r) => setExistingBrands(r.data || [])).catch(() => setExistingBrands([]));
  }, []);

  const activate = async (brand_id) => {
    setLoading(true);
    try {
      await api.post(`/brands/${brand_id}/activate`);
      toast.success("Marca ativada");
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const connectLabrand = async () => {
    setLoading(true);
    try {
      await api.put("/labrand/connect", { api_base: labrandBase, api_key: labrandKey });
      const { data } = await api.get("/labrand/brands");
      setLabrandBrands(data || []);
      toast.success("Conectado! Escolha uma marca.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const listBrands = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/labrand/brands");
      setLabrandBrands(data || []);
      if (!data || data.length === 0) toast("Nenhuma marca no LABrand ainda.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const importFromLabrand = async (id) => {
    setLoading(true);
    try {
      await api.post(`/labrand/import/${id}`);
      toast.success("Marca importada!");
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const createManual = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/brands", { ...form, source: "manual" });
      toast.success("Marca cadastrada!");
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6 lg:p-12" style={{ background: "var(--bg)" }} data-testid="onboarding-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg grad-bg" />
            <div>
              <div className="font-bold">Social Booster</div>
              <div className="text-xs text-muted">olá, {user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 card-2 px-1.5 py-1.5" data-testid="theme-switcher">
              {themes.map((t) => (
                <button key={t} onClick={() => setTheme(t)} data-testid={`theme-${t}`} className={`px-2.5 py-1 rounded-md text-xs ${theme === t ? "grad-bg text-white" : "text-muted"}`}>
                  {({ principal: "◐", claro: "☀", escuro: "☾" })[t]}
                </button>
              ))}
            </div>
            <button onClick={async () => { await logout(); navigate("/login"); }} className="text-xs text-muted hover:text-[var(--txt)]">Sair</button>
          </div>
        </div>

        {step === "choice" && (
          <div className="space-y-8">
            {existingBrands.length > 0 && (
              <div className="card p-5 space-y-3" data-testid="existing-brands-panel">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// suas marcas</div>
                  <h2 className="text-lg font-bold">Escolher uma marca existente</h2>
                  <p className="text-sm text-muted">Você já tem marcas cadastradas. Escolha uma pra continuar.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  {existingBrands.map((b) => (
                    <button key={b.brand_id} onClick={() => activate(b.brand_id)} disabled={loading} className="card-2 hover-lift p-3 flex items-center gap-3 text-left" data-testid={`existing-${b.brand_id}`}>
                      <div className="w-9 h-9 rounded-lg grad-bg flex items-center justify-center text-white font-bold">{(b.name || "?").slice(0, 1).toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{b.name}</div>
                        <div className="text-xs text-muted truncate">{b.source === "labrand" ? "via LABrand" : "manual"}</div>
                      </div>
                      <ArrowRight size={14} className="text-muted" />
                    </button>
                  ))}
                </div>
                <div className="text-xs text-muted">Ou adicione uma nova abaixo ↓</div>
              </div>
            )}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-2">// {existingBrands.length > 0 ? "nova marca" : "primeiro passo"}</div>
              <h1 className="text-4xl font-extrabold mb-2">Você já usa o LABrand?</h1>
              <p className="text-muted">Se sim, puxamos a estratégia da marca automaticamente. Se não, você cadastra manualmente — leva 30 segundos.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <button onClick={() => setStep("labrand")} className="card hover-lift text-left p-6 space-y-3" data-testid="choice-labrand">
                <div className="w-12 h-12 rounded-xl grad-bg flex items-center justify-center">
                  <Link2 className="text-white" size={22} />
                </div>
                <div>
                  <div className="text-lg font-bold flex items-center gap-2">Sim, tenho conta <ArrowRight size={16} /></div>
                  <p className="text-sm text-muted mt-1">Conecta com sua chave da API e escolhe qual marca importar.</p>
                </div>
              </button>
              <button onClick={() => setStep("manual")} className="card hover-lift text-left p-6 space-y-3" data-testid="choice-manual">
                <div className="w-12 h-12 rounded-xl card-2 flex items-center justify-center">
                  <Building2 size={22} />
                </div>
                <div>
                  <div className="text-lg font-bold flex items-center gap-2">Não / ainda não <ArrowRight size={16} /></div>
                  <p className="text-sm text-muted mt-1">Cadastra a marca manualmente. Você pode conectar o LABrand depois.</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {step === "labrand" && (
          <div className="space-y-6" data-testid="onboarding-labrand">
            <button onClick={() => setStep("choice")} className="text-sm text-muted hover:text-[var(--txt)]">← voltar</button>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-2">// integração</div>
              <h2 className="text-3xl font-extrabold mb-2">Conectar LABrand</h2>
              <p className="text-muted">Precisamos da URL base da API e sua chave.</p>
            </div>
            <div className="card p-6 space-y-4 max-w-2xl">
              <div>
                <label className="text-xs text-muted font-mono uppercase tracking-widest">URL base da API</label>
                <input className="input mt-1.5" placeholder="https://labrand.preview.emergentagent.com/api" value={labrandBase} onChange={(e) => setLabrandBase(e.target.value)} data-testid="labrand-base" />
              </div>
              <div>
                <label className="text-xs text-muted font-mono uppercase tracking-widest">API Key</label>
                <input className="input mt-1.5" type="password" placeholder="sua chave secreta" value={labrandKey} onChange={(e) => setLabrandKey(e.target.value)} data-testid="labrand-key" />
              </div>
              <div className="flex gap-3">
                <button onClick={connectLabrand} disabled={loading || !labrandBase || !labrandKey} className="btn-grad" data-testid="labrand-connect">
                  {loading ? "Conectando…" : "Conectar e listar marcas"}
                </button>
                {labrandStatus?.configured && (
                  <button onClick={listBrands} className="btn-outline" data-testid="labrand-refresh">Já conectado — atualizar</button>
                )}
              </div>
            </div>

            {labrandBrands.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-semibold">Escolha a marca a importar:</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {labrandBrands.map((b) => (
                    <button key={b.id || b.brand_id} onClick={() => importFromLabrand(b.id || b.brand_id)} className="card hover-lift text-left p-4 flex items-center gap-3" data-testid={`labrand-brand-${b.id || b.brand_id}`}>
                      <div className="w-10 h-10 rounded-lg grad-bg flex items-center justify-center text-white font-bold">
                        {(b.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{b.name}</div>
                        <div className="text-xs text-muted truncate">{b.industry || "—"}</div>
                      </div>
                      <ArrowRight size={16} className="text-muted" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === "manual" && (
          <div className="space-y-6" data-testid="onboarding-manual">
            <button onClick={() => setStep("choice")} className="text-sm text-muted hover:text-[var(--txt)]">← voltar</button>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-2">// cadastro</div>
              <h2 className="text-3xl font-extrabold mb-2">Sua marca</h2>
              <p className="text-muted">Preenche o básico agora — dá pra editar depois.</p>
            </div>
            <form onSubmit={createManual} className="card p-6 space-y-4 max-w-2xl">
              <div>
                <label className="text-xs text-muted font-mono uppercase tracking-widest">Nome da marca *</label>
                <input className="input mt-1.5" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brand-name" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted font-mono uppercase tracking-widest">@ / handle</label>
                  <input className="input mt-1.5" placeholder="@suamarca" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} data-testid="brand-handle" />
                </div>
                <div>
                  <label className="text-xs text-muted font-mono uppercase tracking-widest">Nicho / indústria</label>
                  <input className="input mt-1.5" placeholder="ex: E-commerce moda" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="brand-industry" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted font-mono uppercase tracking-widest">Público-alvo</label>
                <textarea className="input mt-1.5" rows={3} placeholder="Quem você atende?" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="brand-audience" />
              </div>
              <button type="submit" disabled={loading} className="btn-grad" data-testid="brand-create">
                {loading ? "Salvando…" : "Criar marca e continuar"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
